import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiBookOpen, FiExternalLink, FiCopy, FiGithub, FiCloud, FiDatabase, FiSettings } from 'react-icons/fi';
import SectionTitle from '../components/SectionTitle';
import Card from '../components/Card';

const setupGuides = [
  {
    id: 'github',
    title: 'GitHub Setup',
    icon: FiGithub,
    description: 'Configure GitHub repository and CI/CD',
    steps: [
      'Create a new repository on GitHub',
      'Clone the repository locally',
      'Set up branch protection rules',
      'Configure GitHub Actions for deployment'
    ]
  },
  {
    id: 'cloudflare',
    title: 'Cloudflare Pages',
    icon: FiCloud,
    description: 'Deploy your application to Cloudflare Pages',
    steps: [
      'Connect your GitHub repository',
      'Configure build settings (npm run build)',
      'Set output directory to "dist"',
      'Add environment variables in settings'
    ]
  },
  {
    id: 'database',
    title: 'Database Setup',
    icon: FiDatabase,
    description: 'Configure your chosen database provider',
    steps: [
      'Create account with NoCodeBackend or Supabase',
      'Set up your database schema',
      'Generate API keys and connection strings',
      'Configure environment variables'
    ]
  },
  {
    id: 'environment',
    title: 'Environment Variables',
    icon: FiSettings,
    description: 'Configure all required environment variables',
    steps: [
      'VITE_NCB_INSTANCE_ID - Your NCB instance ID',
      'VITE_DROPBOX_APP_KEY - Dropbox app key (public)',
      'NCB_API_KEY - NCB API key (server-side)',
      'DROPBOX_REFRESH_TOKEN - Dropbox refresh token'
    ]
  }
];

const GuideCard = ({ guide }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const IconComponent = guide.icon;

  const handleCopySteps = () => {
    const stepsText = guide.steps.map((step, index) => `${index + 1}. ${step}`).join('\n');
    navigator.clipboard.writeText(`${guide.title}\n\n${stepsText}`);
    console.log('Guide copied to clipboard');
  };

  return (
    <Card className="hover:shadow-xl transition-all duration-300">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center">
          <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mr-3">
            <IconComponent className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{guide.title}</h3>
            <p className="text-sm text-gray-600">{guide.description}</p>
          </div>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-indigo-600 hover:text-indigo-800 transition-colors"
        >
          {isExpanded ? 'Collapse' : 'Expand'}
        </button>
      </div>

      {isExpanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="border-t border-gray-200 pt-4"
        >
          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
            {guide.steps.map((step, index) => (
              <li key={index} className="leading-relaxed">{step}</li>
            ))}
          </ol>
          
          <div className="flex gap-2 mt-4 pt-4 border-t border-gray-100">
            <button
              onClick={handleCopySteps}
              className="btn-secondary text-xs"
            >
              <FiCopy className="mr-1 h-3 w-3" />
              Copy Steps
            </button>
            <button className="btn-secondary text-xs">
              <FiExternalLink className="mr-1 h-3 w-3" />
              View Docs
            </button>
          </div>
        </motion.div>
      )}
    </Card>
  );
};

function SetupGuides() {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <SectionTitle 
        title="Setup Guides" 
        subtitle="Step-by-step guides to configure all the services and integrations for your project." 
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {setupGuides.map((guide) => (
          <GuideCard key={guide.id} guide={guide} />
        ))}
      </div>

      <Card className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <div className="flex items-start">
          <FiBookOpen className="h-6 w-6 text-blue-600 mt-1 mr-3 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Need More Help?</h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              These guides cover the essential setup steps. For detailed documentation, 
              troubleshooting, and advanced configurations, visit our comprehensive documentation site.
            </p>
            <button className="mt-3 btn-secondary text-sm">
              <FiExternalLink className="mr-2 h-4 w-4" />
              View Full Documentation
            </button>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}

export default SetupGuides;